import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getCurrentUser } from 'api/auth'

const Ctx = createContext(undefined)

export const AuthProvider = ({ children }) => {
  const [session, setSessionState] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Expose setSession for login
  const setSession = (sessionData) => {
    setSessionState(sessionData)
    if (sessionData) {
      localStorage.setItem('auth_session', JSON.stringify(sessionData))
    } else {
      localStorage.removeItem('auth_session')
    }
  }

  useEffect(() => {
    // Check for stored session
    const storedSession = localStorage.getItem('auth_session')
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession)
        setSessionState({
          user: sessionData.user,
          access_token: sessionData.access_token
        })
      } catch (e) {
        console.error('Failed to parse stored session', e)
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    const loadProfile = async () => {
      if (!session?.user || !session?.access_token) {
        setProfile(null)
        return
      }

      try {
        // Fetch profile from API
        const data = await getCurrentUser()
        setProfile({
          id: data.profile.id,
          full_name: data.profile.full_name,
          role: data.profile.role,
          company_name: data.profile.company_name,
        })
      } catch (error) {
        console.error('Failed to load profile:', error)
        // Fallback to stored profile if available
        const storedSession = localStorage.getItem('auth_session')
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession)
            if (sessionData.profile) {
              setProfile(sessionData.profile)
            }
          } catch (e) {
            // Ignore
          }
        }
      }
    }
    loadProfile()
  }, [session?.user?.id, session?.access_token])

  const signOut = async () => {
    try {
      const { logout } = await import('api/auth')
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    localStorage.removeItem('auth_session')
    setSessionState(null)
    setProfile(null)
  }

  const value = useMemo(() => ({
    session,
    user: session?.user ?? null,
    profile,
    loading,
    signOut,
    setSession, // Expose for login
  }), [session, profile, loading])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useAuth = () => {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
